'use client'

import { useProjectPriceChart } from '@repo/services'
import { LineChart, type LineConfig, getCssVar } from '@repo/shared/components'
import { useFeatureFlag, useTranslate } from '@repo/shared/hooks'
import type { ProjectsProject } from '@repo/shared/types'
import { Card, Col, Row, Typography } from '@repo/ui/components'
import {
  SegmentedGroup,
  SegmentedGroupItem,
} from '@repo/ui/components/segmented-group'
import { useIsDesktop } from '@repo/ui/hooks'
import type { UTCTimestamp } from 'lightweight-charts'
import { useTheme } from 'next-themes'
import { useEffect, useMemo, useState } from 'react'
export interface ProjectDetailPriceChartProps {
  project: ProjectsProject
}

const toUnixTimestamp = (isoUtcString: string): UTCTimestamp =>
  Math.floor(new Date(isoUtcString).getTime() / 1000) as UTCTimestamp

export const ProjectDetailPriceChart = ({
  project,
}: ProjectDetailPriceChartProps) => {
  const { t } = useTranslate()
  const [range, setRange] = useState<string>()
  const isDesktop = useIsDesktop()
  const { resolvedTheme } = useTheme()

  const { getBaseTimeRanges } = useFeatureFlag(
    ['charts-project-history', 'value', 'intervals'],
    {
      returnBoolean: false,
    },
  )

  const baseTimeRanges = useMemo(
    () => getBaseTimeRanges?.() || [],
    [getBaseTimeRanges],
  )

  useEffect(() => {
    if (!range && baseTimeRanges.length > 0) {
      setRange(baseTimeRanges[0]?.value || '1d')
    }
  }, [baseTimeRanges, range])

  const { data } = useProjectPriceChart({
    period: range as '30d' | '90d' | '1y',
    token: project?.token || '',
  })

  // LineChart expects { time, value }
  const lineData = useMemo(() => {
    const raw = data?.data?.data || []

    // Default: map 1:1
    return raw.map((item) => ({
      time: toUnixTimestamp(new Date(item.time ?? 0).toISOString()),
      value: item.price ? item.price / 10 : 0,
    }))
  }, [data?.data?.data])

  // State for line visibility
  const [lineVisibility, setLineVisibility] = useState<Record<string, boolean>>(
    {
      'main-line': true,
    },
  )

  const [color, setColor] = useState<string>('#3B82F6')

  useEffect(() => {
    setTimeout(() => {
      setColor(getCssVar('--color-base-main'))
    }, 50)
  }, [resolvedTheme])

  // Multiple lines configuration
  const linesConfig: LineConfig[] = useMemo(() => {
    if (lineData.length === 0) return []

    return [
      {
        id: 'main-line',
        data: lineData,
        label: t('projectsDetail_priceChart_value'),
        color,
        lineType: 'solid' as const,
        hasGradient: true,
        lineWidth: 2,
        visible: lineVisibility['main-line'],
      },
    ]
  }, [lineData, lineVisibility, t, color])

  // Toggle line visibility
  const toggleLineVisibility = (lineId: string) => {
    setLineVisibility((prev) => ({
      ...prev,
      [lineId]: !prev[lineId],
    }))
  }

  return (
    <Card className='gap-lg !px-0 pb-0 sm:p-lg border-none'>
      <Row className='justify-between items-center mb-md'>
        <Typography size='md' weight='bold' className='whitespace-nowrap'>
          {t('projectsDetail_priceChart_title')}
        </Typography>
        <SegmentedGroup
          value={range}
          onValueChange={setRange}
          color='neutral'
          size={isDesktop ? 'md' : 'sm'}
        >
          {baseTimeRanges.map((item) => (
            <SegmentedGroupItem
              key={item.value}
              value={item.value}
              className='md:min-w-[100px] min-w-[50px]'
            >
              {item.label}
            </SegmentedGroupItem>
          ))}
        </SegmentedGroup>
      </Row>
      <Row className='gap-lg items-stretch md:flex-row flex-col flex-wrap'>
        <Col className='rounded-md p-0 ltr min-h-[136px]'>
          <LineChart
            className='w-full h-[136px] sm:h-full'
            lines={linesConfig}
            showCustomTooltip={true}
            formatPriceCompact={!isDesktop}
            range={range}
          />
        </Col>
        <Col className='justify-between sm:justify-center flex-1 w-full md:min-w-[180px] max-w-full md:max-w-[220px] gap-2xl md:flex-col flex-row-reverse'>
          {linesConfig.map((line) => (
            <div
              key={line.id}
              onClick={() => toggleLineVisibility(line.id)}
              className='cursor-pointer'
            >
              <Col className='items-center gap-xs w-fit sm:w-full hover:opacity-80 transition-opacity'>
                <span
                  className='cursor-pointer inline-block w-8 h-1 rounded bg-transparent'
                  style={{
                    backgroundColor: line.color,
                    opacity: line.visible ? 1 : 0.3,
                  }}
                />
                <Typography
                  size='2xs'
                  color={
                    line.visible
                      ? 'text-neutral-surface-60'
                      : 'text-neutral-surface-40'
                  }
                  className={line.visible ? '' : 'line-through'}
                >
                  {line.label}
                </Typography>
              </Col>
            </div>
          ))}
        </Col>
      </Row>
    </Card>
  )
}
